// Phase-A R1.1 — Route classification. Extracted from router.tsx so
// AppShell can consume the truth table without duplicating it.
// WORKSPACE by elimination = deny-by-default (Phase 15 RBAC extends with CASL).

export type RouteKind = "PUBLIC" | "AUTH_FLOW" | "WORKSPACE";

const PUBLIC_ROUTE_IDS: ReadonlySet<string> = new Set([
  "",
  "home",
  "programs",
  "about",
  "pricing",
  "admissions",
  // Phase B R6 (D80, Q1.a) — the public anon applicant surfaces. These
  // are the first anon *writable* routes (a form + a token status page);
  // PUBLIC classification keeps the AppShell auth gate from forcing a
  // login (which would break the anon premise). No new RouteKind.
  "apply",
  "track",
  // R-Notif (D100) — public password-claim landing (/claim?token=). Same anon
  // premise as apply/track: the applicant has no session yet, so PUBLIC keeps
  // the AppShell auth gate from forcing a login.
  "claim",
  // R-AUTH-3 — public password-reset landing (/reset?token=). Same token-based
  // anon premise as /claim.
  "reset",
  "help",
  "honor-code",
  // R-SETTINGS — the PUBLIC privacy-gated profile card (/u/:userId). Reachable
  // LOGGED-OUT (a "public" profile renders for guests); the API + page enforce
  // the owner's privacy (private → 404, members → 401 for a guest). PUBLIC
  // classification keeps the AppShell auth gate from bouncing a guest to /login.
  "u",
  // R-MARKET P4 — «بازار دانش» PUBLIC marketplace. The catalog (/market) + a
  // course's public detail (/market/course/:id) are reachable LOGGED-OUT (doc
  // §7.1: every role + a guest can browse). NOTE: the route id is the FIRST path
  // segment only, so this set entry alone would ALSO flip the authed-only
  // /market/studio* (authoring) and /market/review* (reviewer) subtrees to
  // PUBLIC and disable their auth gate. getRouteKind() below special-cases the
  // "market" segment on its sub-path so ONLY the bare catalog + /market/course/*
  // are PUBLIC while studio/review stay WORKSPACE (auth-gated). Defence in depth:
  // route-roles.ts still gates studio/review by role, and the API @Roles-guards.
  "market",
]);

// R-MARKET P4/P5 — the "market" first segment is SPLIT: the public marketplace
// (bare /market catalog + /market/course/:id detail) is PUBLIC, but the authoring
// (/market/studio*), reviewer (/market/review*) and economics (/market/my-courses,
// /market/earnings, /market/admin — P5) subtrees must stay WORKSPACE (auth-gated).
// True ⇒ this market sub-path is one of those authed-only surfaces.
const AUTHED_MARKET_SUBPATHS: ReadonlySet<string> = new Set([
  "studio",
  "review",
  // R-MARKET P5 — pricing/purchase/economics
  "my-courses",
  "earnings",
  "admin",
  // R-MARKET EXT — the buyer's wishlist (/market/wishlist). WORKSPACE (auth-gated);
  // route-roles "market/wishlist" opens it to ANY authed role.
  "wishlist",
  // R-MARKET P6 — the enrolled buyer's course player (/market/learn/:id). WORKSPACE
  // (auth-gated); the API 404s a non-enrolled caller. route-roles "market/learn"
  // opens it to ANY authed role (every role may have a purchase).
  "learn",
  // R-MARKET OPEN-RESPONSE (Gap 9) — the author STAFF grading queue (/market/grading-queue/:id).
  // WORKSPACE (auth-gated); route-roles "market/grading-queue" gates it to author roles.
  "grading-queue",
]);
const isAuthedMarketSubPath = (subPath: string | undefined): boolean => {
  if (!subPath) return false; // bare /market → public catalog
  const first = subPath.split("/")[0];
  return AUTHED_MARKET_SUBPATHS.has(first);
};

const AUTH_FLOW_ROUTE_IDS: ReadonlySet<string> = new Set([
  "login",
  "register",
  "forgot",
  "verify-email",
  "2fa-setup",
  "onboarding",
]);

// `subPath` is the route's path beyond the first segment (AppShell passes its
// `routeParam`). It only changes the answer for the SPLIT "market" segment
// (R-MARKET P4); every other route classifies on the first segment alone, so
// existing single-arg callers keep their behaviour.
export const getRouteKind = (routeId: string, subPath?: string): RouteKind => {
  // R-MARKET P4 — keep the authed-only studio/review market subtrees WORKSPACE
  // even though the shared "market" segment is in PUBLIC_ROUTE_IDS for the catalog.
  if (routeId === "market" && isAuthedMarketSubPath(subPath)) return "WORKSPACE";
  if (PUBLIC_ROUTE_IDS.has(routeId)) return "PUBLIC";
  if (AUTH_FLOW_ROUTE_IDS.has(routeId)) return "AUTH_FLOW";
  return "WORKSPACE";
};

export const isPublicRoute = (routeId: string, subPath?: string): boolean =>
  getRouteKind(routeId, subPath) === "PUBLIC";

export const isAuthFlowRoute = (routeId: string, subPath?: string): boolean =>
  getRouteKind(routeId, subPath) === "AUTH_FLOW";

export const isWorkspaceRoute = (routeId: string, subPath?: string): boolean =>
  getRouteKind(routeId, subPath) === "WORKSPACE";
